import { useMemo } from 'react';
import loadable from '@loadable/component';

import { Heading } from '@simplywallst/ui-core/next';
import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';

import Tooltip from '@/components/Tooltip';

import { DownloadCSVButton } from '../DownloadCSVButton';
import { ScrollToTop } from '../ScrollToTop';
import { PopulatedState as Table } from '../Table';
import { PopulatedState as Charts } from '../Charts';
import { TransactionPortfolioUpgrade } from '../TransactionPortfolioUpgrade';

import { useSortedHoldings } from '../../hooks/useSortedHoldings';

import type { PortfolioDetailedReturns } from '../../hooks/usePortfolioDetailedReturns';
import { usePortfolioType } from '../../../HoldingsTable/hooks';

const TransactionPortfolioPrompt = loadable(
  () => import('../TransactionPortfolioStickyBanner'),
  { resolveComponent: (c) => c.TransactionPortfolioPrompt }
);

type Props = {
  portfolioDetailedReturns: PortfolioDetailedReturns;
};

export const PopulatedState = ({ portfolioDetailedReturns }: Props) => {
  const { sortedHoldings, sortedLiquidatedHoldings } = useSortedHoldings(
    portfolioDetailedReturns
  );

  const portfolioType = usePortfolioType(portfolioDetailedReturns);

  const tableProps = useMemo(
    () => ({
      portfolioDetailedReturns: {
        ...portfolioDetailedReturns,
        holdings: sortedHoldings,
        liquidatedHoldings: sortedLiquidatedHoldings,
      },
    }),
    [sortedLiquidatedHoldings, sortedHoldings, portfolioDetailedReturns]
  );

  const { id, detailedReturns } = portfolioDetailedReturns;
  const { startDate, endDate } = detailedReturns;

  const isNonTransactionalPortfolio = portfolioType !== 'transaction-portfolio';

  return (
    <>
      <div className="mb-x3 w-full max-w-screen-xl gap-x2 justify-self-center md:px-x2 lg:mb-x4 lg:px-x3">
        <Charts portfolioDetailedReturns={portfolioDetailedReturns} />
      </div>
      <div className="grid w-full max-w-screen-xl grid-cols-[max-content_max-content] justify-between gap-x2 justify-self-center p-x2 md:pt-0 lg:mb-x2 lg:p-0 lg:px-x3">
        <div className="mb-x1 grid gap-x1 md:grid-cols-[max-content_max-content] md:items-center">
          <Heading
            as="h2"
            size="large"
            weight="strong"
            className="grid grid-cols-[max-content_max-content] items-center justify-between gap-x-1 text-solid"
          >
            <div className="grid grid-cols-[max-content_24px] items-center gap-x-1">
              <span className="md:hidden">Detailed Returns</span>
              <span className="hidden md:inline">Detailed Returns Report</span>
              <Tooltip
                title={
                  <div className="max-w-[260px]">
                    <p className="m-0 text-left text-tiny text-legacy-spacegray">
                      This report shows a full breakdown of your portfolio's
                      total return and how each holding contributed over the
                      selected period.
                    </p>
                  </div>
                }
                light
                placement="top"
                zIndex={99999}
              >
                <InformationSolid className="fill-subtle" />
              </Tooltip>
            </div>
          </Heading>
          {isNonTransactionalPortfolio && <TransactionPortfolioUpgrade />}
        </div>
        <DownloadCSVButton
          portfolioId={id}
          start={new Date(startDate)}
          end={new Date(endDate)}
          isDisabled={isNonTransactionalPortfolio}
        />
      </div>

      <ScrollToTop>
        <div
          className="w-screen overflow-x-auto bg-p-white-100 xl:w-full xl:overflow-x-visible"
          data-theme="light"
        >
          <Table {...tableProps} />
        </div>
      </ScrollToTop>
      {isNonTransactionalPortfolio && <TransactionPortfolioPrompt />}
    </>
  );
};
